import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon } from '@radix-ui/react-icons';

interface MultiSelectProps {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value: externalValue = [],
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(externalValue);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync with external value changes
  useEffect(() => {
    setSelectedValues(externalValue);
  }, [externalValue]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: string) => {
    const newValues = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];

    setSelectedValues(newValues);
    onChange(newValues);
  };

  return (
    <div className="relative w-full h-full" ref={containerRef}>
      {/* Trigger */}
      <div
        onClick={toggleDropdown}
        className={`w-full h-full border-0 bg-transparent focus:ring-0 focus:outline-none cursor-pointer
          ${selectedValues.length > 0 ? 'pt-5 pb-1' : 'px-3'} px-3 flex items-center justify-between`}
      >
        <div className="flex-1 text-gray-800 text-sm">
          {selectedValues.length > 0 ? selectedValues.join(', ') : ''}
        </div>
        <div className="flex items-center justify-center">
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform text-zinc-900 ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="py-1 max-h-60 overflow-auto">
            {options.map((option) => (
              <div
                key={option}
                onClick={() => handleOptionClick(option)}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between
                  ${selectedValues.includes(option) ? 'bg-gray-100' : ''}`}
              >
                <span>{option}</span>
                {selectedValues.includes(option) && (
                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
